import { useMemo, useState } from 'react'
import './statusCard.scss'
import { useTranslation } from 'react-i18next'
import { vehicleNatureValuesDict } from '../../types/constants'

interface StatusCardProps {
  type: string
  activeItems: number
  data: Record<string, number>
  datasets?: number[]
  allLabels?: string[]
  activeContractsAmount?: number
  expiringContractsAmount?: number
  initializedContractsAmount?: number
  totalExpiringFilterWithinDays?: string
  company: boolean
  handleRedirect: () => void
  customChild?: React.ReactElement
}

export default function StatusCard(props: StatusCardProps) {
  const {
    type,
    data,
    activeItems,
    activeContractsAmount,
    expiringContractsAmount,
    initializedContractsAmount,
    totalExpiringFilterWithinDays,
    company,
    handleRedirect,
    customChild,
  } = props
  const [showBack, setShowBack] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const itemsPerPage = 4
  const { t } = useTranslation('common')

  const itemsToDisplay = useMemo(() => {
    const startIndex = currentPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return Object.keys(data)
      .slice(startIndex, endIndex)
      .map((key) => key)
  }, [currentPage, data])

  function handleInfoClick() {
    setShowBack(!showBack)
  }

  function isRedirectEnabled(): boolean {
    if (!company) {
      return false
    }
    if (company && (activeContractsAmount || expiringContractsAmount || initializedContractsAmount)) {
      return true
    }
    return false
  }

  function getCalculatedData() {
    if (type === 'active') {
      return activeContractsAmount
    }
    if (type === 'initialized') {
      return initializedContractsAmount
    }
    if (type === 'expiring') {
      return expiringContractsAmount
    }
  }

  const toggleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }
  const togglePreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1)
  }

  function getTitle(): string {
    if (type === 'expiring') {
      return t('EXPIRING_CONTRACTS')
    } else {
      return t(type.toUpperCase())
    }
  }

  function getExpirationDate() {
    return Number(totalExpiringFilterWithinDays) <= 90
      ? `${totalExpiringFilterWithinDays} ${t('DAYS')}`
      : `${Number(totalExpiringFilterWithinDays) / 30}  ${t('MONTHS')}`
  }
  function getCoverIcon() {
    if (type === 'active') {
      return 'icon-contract'
    }
    if (type === 'initialized') {
      return 'icon-pending'
    }
    if (type === 'expiring') {
      return 'icon-expiring'
    }
  }
  return (
    <>
      <div className={`statusCard ${type ? type : ''}`}>
        <div className={`card-inner ${showBack ? 'rotate-in' : ''}`}>
          <div className="box card-front">
            {Object.entries(data).length > 0 && <i className="icon-zoom-in" onClick={handleInfoClick} />}
            <div className="flex">
              {isRedirectEnabled() ? (
                <a onClick={handleRedirect} className={`${getCoverIcon()} rounded`}></a>
              ) : (
                <i className={`${getCoverIcon()} rounded`}></i>
              )}
              <div className="contract-abstract">
                <strong>{getCalculatedData()}</strong>
                {type === 'expiring' ? `${getTitle()} {${getExpirationDate()}}` : getTitle()}
              </div>
              {customChild}
            </div>
          </div>
          <div className="box card-back">
            <div className="detailStatusTitle">
              {getTitle()}: {getCalculatedData()}
            </div>
            <i className="icon-zoom-out" onClick={handleInfoClick} />
            <div className=" detailedStats">
              {itemsToDisplay.map((item) => {
                return (
                  <div className="flex" key={item}>
                    <i className={vehicleNatureValuesDict[item as keyof typeof vehicleNatureValuesDict]} />
                    <div>
                      {data[item]}
                      <span>{t(item.toUpperCase())}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="navigation">
              {currentPage > 0 && <i className="icon-left" onClick={togglePreviousPage}></i>}
              {activeItems > (currentPage + 1) * 4 && <i className="icon-right" onClick={toggleNextPage}></i>}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
